/**
 * File Stream Service
 * 
 * @remarks
 * Provides secure, authenticated file streaming with support for:
 * - Direct file streaming with byte-range requests
 * - Temporary access token generation
 * - File format conversion
 * - Progress tracking for large files
 * 
 * All operations require Firebase authentication and organization membership.
 * 
 * @example
 * ```typescript
 * // Stream a file
 * const stream$ = await this.fileStreamService.streamFile(fileId);
 * stream$.subscribe(blob => {
 *   const url = URL.createObjectURL(blob);
 *   // Use the URL for display or download
 * });
 * 
 * // Convert a file
 * this.fileStreamService.convertFile(fileId, { format: 'html' })
 *   .subscribe(result => {
 *     this.htmlContent = result.content;
 *   });
 * ```
 * 
 * @see FILE_STREAMING_API.md
 * @see FILE_ACCESS_TOKEN_API.md
 * @see FILE_CONVERSION_API_DOCUMENTATION.md
 */

import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders, HttpResponse, HttpEvent, HttpEventType } from '@angular/common/http';
import { Observable, throwError, from } from 'rxjs';
import { catchError, map, switchMap, filter } from 'rxjs/operators';
import { FIREBASE_AUTH } from '../../fireauth-lib.config';
import { USER_FILES_API_CONFIG } from 'cms-post-lib';
import {
  StreamOptions,
  FileStreamMetadata,
  FileStreamResult,
  GenerateAccessTokenRequest,
  AccessTokenResponse,
  FileAccessTokensResponse,
  RevokeTokensResponse,
  ConversionOptions,
  ConversionResult,
  ApiResponse
} from '../models/file-streaming.model';

/**
 * Service for secure file streaming, access token management, and file conversion
 */
@Injectable({
  providedIn: 'root'
})
export class FileStreamService {
  private readonly http = inject(HttpClient);
  private readonly auth = inject(FIREBASE_AUTH);
  private readonly config = inject(USER_FILES_API_CONFIG);
  private readonly baseUrl = `${this.config.apiBaseUrl}/api/user-files`;

  constructor() {}

  // ============================================================================
  // File Streaming Methods
  // ============================================================================

  /**
   * Stream a file from the API with optional byte-range support
   * 
   * @remarks
   * Streams file content directly from Firebase Storage through the backend API.
   * Supports partial content requests for large files and video streaming.
   * Requires valid Firebase authentication and organization membership.
   * 
   * @param fileId - MongoDB ObjectId of the file
   * @param options - Optional streaming configuration
   * @returns Observable that emits the file as a Blob
   * 
   * @throws {Error} If authentication fails or user lacks permission
   * 
   * @example
   * ```typescript
   * // Stream entire file
   * const stream$ = await this.streamFile(fileId);
   * stream$.subscribe(blob => {
   *   const url = URL.createObjectURL(blob);
   *   window.open(url);
   * });
   * 
   * // Stream with byte range (first 1MB)
   * const stream$ = await this.streamFile(fileId, { start: 0, end: 1048575 });
   * ```
   */
  async streamFile(fileId: string, options: StreamOptions = {}): Promise<Observable<Blob>> {
    const token = await this.getAuthToken();
    const headers = this.buildHeaders(token, options);

    return this.http.get(`${this.baseUrl}/${fileId}/stream`, {
      headers,
      responseType: 'blob',
      observe: 'body'
    }).pipe(
      catchError(error => this.handleError(error, 'streaming file'))
    );
  }

  /**
   * Stream a file with full metadata from response headers
   * 
   * @remarks
   * Similar to streamFile but returns both the Blob and extracted metadata
   * from response headers (filename, size, content-range, etc.)
   * 
   * @param fileId - MongoDB ObjectId of the file
   * @param options - Optional streaming configuration
   * @returns Observable that emits FileStreamResult with blob and metadata
   * 
   * @example
   * ```typescript
   * const result$ = await this.streamFileWithMetadata(fileId);
   * result$.subscribe(({ blob, metadata }) => {
   *   console.log('File:', metadata.fileName);
   *   console.log('Size:', metadata.fileSize);
   *   const url = URL.createObjectURL(blob);
   * });
   * ```
   */
  async streamFileWithMetadata(
    fileId: string,
    options: StreamOptions = {}
  ): Promise<Observable<FileStreamResult>> {
    const token = await this.getAuthToken();
    const headers = this.buildHeaders(token, options);

    return this.http.get(`${this.baseUrl}/${fileId}/stream`, {
      headers,
      responseType: 'blob',
      observe: 'response'
    }).pipe(
      map(response => ({
        blob: response.body!,
        metadata: this.extractMetadata(response)
      })),
      catchError(error => this.handleError(error, 'streaming file with metadata'))
    );
  }

  /**
   * Stream a file with download progress tracking
   * 
   * @remarks
   * Provides progress events during download, useful for large files.
   * Emits progress percentage (0-100) and final Blob.
   * 
   * @param fileId - MongoDB ObjectId of the file
   * @param options - Optional streaming configuration
   * @returns Observable that emits progress numbers and final Blob
   * 
   * @example
   * ```typescript
   * const stream$ = await this.streamFileWithProgress(fileId);
   * stream$.subscribe(event => {
   *   if (typeof event === 'number') {
   *     this.progress = event;
   *   } else {
   *     const url = URL.createObjectURL(event);
   *   }
   * });
   * ```
   */
  async streamFileWithProgress(
    fileId: string,
    options: StreamOptions = {}
  ): Promise<Observable<number | Blob>> {
    const token = await this.getAuthToken();
    const headers = this.buildHeaders(token, options);

    return this.http.get(`${this.baseUrl}/${fileId}/stream`, {
      headers,
      responseType: 'blob',
      observe: 'events',
      reportProgress: true
    }).pipe(
      map(event => {
        if (event.type === HttpEventType.DownloadProgress) {
          const progress = event.total 
            ? Math.round((100 * event.loaded) / event.total)
            : 0;
          return progress;
        } else if (event.type === HttpEventType.Response) {
          return event.body!;
        }
        return null;
      }),
      filter(value => value !== null),
      map(value => value as number | Blob),
      catchError(error => this.handleError(error, 'streaming file with progress'))
    );
  }

  // ============================================================================
  // Access Token Methods
  // ============================================================================

  /**
   * Generate a temporary access token for a file
   * 
   * @remarks
   * Creates a time-limited JWT token for file access.
   * Useful for sharing files with temporary access or embedding in iframes.
   * Tokens expire between 1 minute (60s) and 1 hour (3600s).
   * 
   * @param fileId - MongoDB ObjectId of the file
   * @param expiresIn - Token expiration time in seconds (60-3600)
   * @returns Observable that emits the access token response
   * 
   * @example
   * ```typescript
   * // Generate 30-minute token
   * this.generateAccessToken(fileId, 1800).subscribe(response => {
   *   const tokenUrl = `${baseUrl}/stream?token=${response.accessToken}`;
   *   console.log('Expires at:', response.expiresAt);
   * });
   * ```
   */
  generateAccessToken(
    fileId: string,
    expiresIn: number
  ): Observable<AccessTokenResponse> {
    if (expiresIn < 60 || expiresIn > 3600) {
      return throwError(() => new Error('expiresIn must be between 60 and 3600 seconds'));
    }

    const request: GenerateAccessTokenRequest = { expiresIn };

    return from(this.getAuthToken()).pipe(
      switchMap(token => {
        const headers = new HttpHeaders().set('Authorization', `Bearer ${token}`);
        return this.http.post<ApiResponse<AccessTokenResponse>>(
          `${this.baseUrl}/${fileId}/access-token`,
          request,
          { headers }
        );
      }),
      map(response => response.data!),
      catchError(error => this.handleError(error, 'generating access token'))
    );
  }

  /**
   * Get all active access tokens for a file
   * 
   * @remarks
   * Retrieves list of all currently valid access tokens.
   * Only accessible by organization members.
   * 
   * @param fileId - MongoDB ObjectId of the file
   * @returns Observable that emits list of active tokens
   * 
   * @example
   * ```typescript
   * this.getFileAccessTokens(fileId).subscribe(response => {
   *   console.log(`${response.count} active tokens`);
   *   response.tokens.forEach(token => {
   *     console.log('Expires:', token.expiresAt);
   *   });
   * });
   * ```
   */
  getFileAccessTokens(fileId: string): Observable<FileAccessTokensResponse> {
    return from(this.getAuthToken()).pipe(
      switchMap(token => {
        const headers = new HttpHeaders().set('Authorization', `Bearer ${token}`);
        return this.http.get<ApiResponse<FileAccessTokensResponse>>(
          `${this.baseUrl}/${fileId}/access-tokens`,
          { headers }
        );
      }),
      map(response => response.data!),
      catchError(error => this.handleError(error, 'fetching access tokens'))
    );
  }

  /**
   * Revoke all active access tokens for a file
   * 
   * @remarks
   * Invalidates all currently active tokens for the file.
   * Useful when access should be immediately terminated.
   * 
   * @param fileId - MongoDB ObjectId of the file
   * @returns Observable that emits revocation result
   * 
   * @example
   * ```typescript
   * this.revokeFileAccessTokens(fileId).subscribe(response => {
   *   console.log(`Revoked ${response.revokedCount} tokens`);
   * });
   * ```
   */
  revokeFileAccessTokens(fileId: string): Observable<RevokeTokensResponse> {
    return from(this.getAuthToken()).pipe(
      switchMap(token => {
        const headers = new HttpHeaders().set('Authorization', `Bearer ${token}`);
        return this.http.delete<ApiResponse<RevokeTokensResponse>>(
          `${this.baseUrl}/${fileId}/access-tokens`,
          { headers }
        );
      }),
      map(response => response.data!),
      catchError(error => this.handleError(error, 'revoking access tokens'))
    );
  }

  /**
   * Generate a quick access token (5 minutes)
   * 
   * @remarks
   * Convenience method for short-lived tokens.
   * Ideal for temporary file previews.
   * 
   * @param fileId - MongoDB ObjectId of the file
   * @returns Observable that emits the access token
   */
  generateQuickAccessToken(fileId: string): Observable<AccessTokenResponse> {
    return this.generateAccessToken(fileId, 300); // 5 minutes
  }

  /**
   * Generate a standard access token (30 minutes)
   * 
   * @remarks
   * Convenience method for medium-duration tokens.
   * Suitable for most file viewing scenarios.
   * 
   * @param fileId - MongoDB ObjectId of the file
   * @returns Observable that emits the access token
   */
  generateStandardAccessToken(fileId: string): Observable<AccessTokenResponse> {
    return this.generateAccessToken(fileId, 1800); // 30 minutes
  }

  /**
   * Generate an extended access token (1 hour)
   * 
   * @remarks
   * Convenience method for long-duration tokens.
   * Use for extended viewing sessions.
   * 
   * @param fileId - MongoDB ObjectId of the file
   * @returns Observable that emits the access token
   */
  generateExtendedAccessToken(fileId: string): Observable<AccessTokenResponse> {
    return this.generateAccessToken(fileId, 3600); // 1 hour
  }

  // ============================================================================
  // File Conversion Methods
  // ============================================================================

  /**
   * Convert a file to a web-viewable format
   * 
   * @remarks
   * Converts office documents to HTML, PDF, or text format.
   * Results are cached by default for better performance.
   * 
   * Supported conversions:
   * - DOCX → HTML, text
   * - XLSX → HTML, text (supports page parameter for sheet selection)
   * - PPTX → PDF, text
   * - TXT/CSV → HTML, text
   * 
   * @param fileId - MongoDB ObjectId of the file
   * @param options - Conversion configuration
   * @returns Observable that emits the conversion result
   * 
   * @throws {Error} If conversion is not supported for the file type
   * 
   * @example
   * ```typescript
   * // Convert DOCX to HTML
   * this.convertFile(fileId, { format: 'html' }).subscribe(result => {
   *   this.htmlContent = result.content;
   *   console.log('Cached:', result.cached);
   * });
   * 
   * // Convert Excel sheet 2 to HTML
   * this.convertFile(fileId, { format: 'html', page: 2 }).subscribe(result => {
   *   this.sheetHtml = result.content;
   * });
   * ```
   */
  convertFile(
    fileId: string,
    options: ConversionOptions
  ): Observable<ConversionResult> {
    if (!options.format) {
      return throwError(() => new Error('Conversion format is required'));
    }

    const params: any = { format: options.format };
    if (options.page !== undefined) {
      params.page = options.page.toString();
    }
    if (options.cache !== undefined) {
      params.cache = options.cache.toString();
    }

    return from(this.getAuthToken()).pipe(
      switchMap(token => {
        const headers = new HttpHeaders().set('Authorization', `Bearer ${token}`);
        return this.http.get<ApiResponse<ConversionResult>>(
          `${this.baseUrl}/${fileId}/convert`,
          { headers, params }
        );
      }),
      map(response => response.data!),
      catchError(error => this.handleError(error, 'converting file'))
    );
  }

  /**
   * Invalidate the conversion cache for a file
   * 
   * @remarks
   * Clears all cached conversion results for the file.
   * Useful after file updates or to force re-conversion.
   * 
   * @param fileId - MongoDB ObjectId of the file
   * @returns Observable that completes when cache is cleared
   * 
   * @example
   * ```typescript
   * this.invalidateConversionCache(fileId).subscribe(() => {
   *   console.log('Cache cleared');
   *   // Now convert again to get fresh result
   *   this.convertFile(fileId, { format: 'html' });
   * });
   * ```
   */
  invalidateConversionCache(fileId: string): Observable<void> {
    return from(this.getAuthToken()).pipe(
      switchMap(token => {
        const headers = new HttpHeaders().set('Authorization', `Bearer ${token}`);
        return this.http.delete<ApiResponse<any>>(
          `${this.baseUrl}/${fileId}/conversion-cache`,
          { headers }
        );
      }),
      map(() => undefined),
      catchError(error => this.handleError(error, 'invalidating conversion cache'))
    );
  }

  // ============================================================================
  // Helper Methods
  // ============================================================================

  /**
   * Get Firebase authentication token
   * 
   * @returns Promise that resolves to the current user's ID token
   * @throws {Error} If user is not authenticated
   */
  private async getAuthToken(): Promise<string> {
    const user = this.auth.currentUser;
    if (!user) {
      throw new Error('User not authenticated');
    }
    return await user.getIdToken();
  }

  /**
   * Build HTTP headers with authentication and optional byte range
   * 
   * @param token - Firebase ID token
   * @param options - Stream options with optional byte range
   * @returns HttpHeaders object
   */
  private buildHeaders(token: string, options: StreamOptions): HttpHeaders {
    let headers = new HttpHeaders().set('Authorization', `Bearer ${token}`);

    if (options.start !== undefined && options.end !== undefined) {
      headers = headers.set('Range', `bytes=${options.start}-${options.end}`);
    }

    return headers;
  }

  /**
   * Extract metadata from HTTP response headers
   * 
   * @param response - HTTP response with headers
   * @returns FileStreamMetadata extracted from headers
   */
  private extractMetadata(response: HttpResponse<Blob>): FileStreamMetadata {
    const contentDisposition = response.headers.get('Content-Disposition') || '';
    const fileName = this.extractFileName(contentDisposition);
    const fileType = response.headers.get('Content-Type') || 'application/octet-stream';
    const contentLength = response.headers.get('Content-Length');
    const contentRange = response.headers.get('Content-Range');

    const metadata: FileStreamMetadata = {
      fileName,
      fileType
    };

    if (contentLength) {
      metadata.fileSize = parseInt(contentLength, 10);
    } else if (contentRange) {
      // Extract total size from Content-Range header (e.g., "bytes 0-1023/1048576")
      const match = contentRange.match(/\/(\d+)$/);
      if (match) {
        metadata.fileSize = parseInt(match[1], 10);
      }
      metadata.contentRange = contentRange;
    }

    return metadata;
  }

  /**
   * Extract filename from Content-Disposition header
   * 
   * @param contentDisposition - Content-Disposition header value
   * @returns Extracted filename or default
   */
  private extractFileName(contentDisposition: string): string {
    const match = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
    if (match && match[1]) {
      return match[1].replace(/['"]/g, '');
    }
    return 'download';
  }

  /**
   * Handle HTTP errors with context
   * 
   * @param error - HTTP error response
   * @param context - Operation context for error message
   * @returns Observable that emits the error
   */
  private handleError(error: any, context: string): Observable<never> {
    let errorMessage = `Error ${context}: `;

    if (error.error instanceof ErrorEvent) {
      // Client-side error
      errorMessage += error.error.message;
    } else {
      // Server-side error
      const statusCode = error.status || 'unknown';
      const message = error.error?.message || error.error?.error || error.message || 'Unknown error';
      errorMessage += `${message} (Status: ${statusCode})`;
    }

    console.error(errorMessage, error);
    return throwError(() => new Error(errorMessage));
  }
}
